package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.Button
import top.wcpe.allincore.ui.runtime.element.UIElement

/**
 * web 预览简化按钮渲染器（FR-UI-024 补 JS 端 button 可视缺口）。
 *
 * 完整 [ButtonRenderer]（theme / gradient / mask / ripple / neon）依赖 ControlTheme /
 * StateLayerCompositor 等仍留 jvmMain（ADR-0072 决策 4「留守渲染器」，未迁 commonMain），
 * 故 JS 端 `registerLeaveBehindRenderers` 原为空、button 无渲染器不可见。此处补一个简化形态：
 * 背景色块（元素无背景色时用默认按钮蓝）+ label 文字居中。不追求与 fabric 像素一致——
 * 同 item/entity/slot 占位框的预览降级精神（ADR-0068 §6）。
 */
internal class SimpleButtonRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val button = element as Button
        val x = box.x()
        val y = box.y()
        val w = box.width()
        val h = box.height()

        // 背景：优先元素自身背景色；透明时用默认按钮蓝（预览无主题，给可辨识底色）
        val bg = element.style().backgroundColor()
        val fill = if ((bg ushr ALPHA_SHIFT) != 0) bg else DEFAULT_BUTTON_BG
        bridge.fillRect(x, y, w, h, fill)

        // label 居中（水平按测宽居中，垂直按字高居中）
        val label = button.text()
        if (label.isNotEmpty()) {
            val tw = bridge.textWidth(label, LABEL_SCALE)
            val tx = x + (w - tw) / 2f
            val ty = y + (h - GLYPH_HEIGHT * LABEL_SCALE) / 2f
            bridge.drawText(label, tx, ty, button.color(), LABEL_SCALE)
        }
    }

    private companion object {
        /** ARGB alpha 字节位移。 */
        private const val ALPHA_SHIFT = 24

        /** MC 字体单行字高约 8px，用于 label 垂直居中。 */
        private const val GLYPH_HEIGHT = 8f

        /** label 绘制缩放（1 = 原尺寸）。 */
        private const val LABEL_SCALE = 1f

        /** 预览默认按钮底色（无背景色时的可辨识蓝）。 */
        private val DEFAULT_BUTTON_BG = 0xFF3B6EA5.toInt()
    }
}
